import React, {useState, useMemo} from 'react';
import styled, {css} from 'styled-components';
import SbEditable from 'components/SbEditableExtended';

import ContentWrapper from 'components/contentWrapper';
import WhiteBox from 'components/blocks/blockPartials/whiteBox';
import GalleryModal from 'components/galleryModal';

import theme, {bp, color} from 'settings/theme';

import {multiplyValueWithUnits} from 'utils/units';
import calculateBottomSpacingClass from 'components/blocks/utils/calculateBottomSpacingClass';
import multiOptionsToObject from 'components/blocks/utils/multiOptionsToObject';


const createImageUrl = (origUrl, width) => {
  return origUrl && typeof origUrl === 'string' ? `${origUrl}/m/${width}x0/smart` : ''
}



// Component
const Component = (props) => {

  const displaySettings = useMemo(() => multiOptionsToObject(props.block?.display_settings), [props.block?.display_settings])
  const spacingBottomClass = useMemo(() => calculateBottomSpacingClass(props.block?.bottom_spacing), [props.block?.bottom_spacing])

  const [galleryState, setGalleryState] = useState({
    slideIndex: 0,
    isOpen: false,
  })

  const openGallery = (e, slideIndex) => {
    setGalleryState({
      slideIndex,
      isOpen: true
    })
  }


  if(!props.block){
    return(<div>Storyblok block missing</div>)
  }

	return(
    <SbEditable content={props.block}>
      <StyledComponent {...props} displaySettings={displaySettings} className={`blockImageGallery ${spacingBottomClass}`}>
        <WhiteBox 
            removeWhiteBox={displaySettings.removeWhiteBox} 
            className="blockImageGallery-whiteBox paddingVerticalAsSpacing"
        >
          <ContentWrapper className="blockImageGallery-contentWrapper" fullWidthAt={[1]}>

            <div className="blockImageGallery-grid">
                {props.block.images.map((image, index) => (
                    
                  <div className="blockImageGallery-grid-item" key={index}>
                    <button onClick={(e) => openGallery(e, index)}>
                      <div className="blockImageGallery-image">
                        <img src={createImageUrl(image.image.filename, 640)} title={image.caption || 'Photo'} alt={image.image_alt_text || 'Photo'} />
                      </div>
                      {displaySettings.showCaptionsBelowThumbnails && image.caption &&
                        <div className="blockImageGallery-item-caption">
                          <span>{image.caption}</span>
                        </div>
                      }
                    </button>
                  </div>
                  
                ))}
            </div>

            {props.block.caption &&
              <div className="blockImageGallery-caption">
                {props.block.caption}
              </div>
            }

          </ContentWrapper>
        </WhiteBox>
      </StyledComponent>

      {galleryState.isOpen && (

        <GalleryModal
          currentSlide={{
            type: 'image',
            src: createImageUrl(props.block.images[galleryState.slideIndex].image.filename, 640),
            title: props.block.images[galleryState.slideIndex].caption ?? 'Photo',
            alt_text: props.block.images[galleryState.slideIndex].image_alt_text ?? 'Photo'
          }}
          onCloseRequest={() => setGalleryState({ slideIndex:0, isOpen:false })}
          onMovePrevRequest={() =>
            setGalleryState({
              slideIndex: (galleryState.slideIndex + props.block.images.length - 1) % props.block.images.length,
              isOpen: galleryState.isOpen
            })
          }
          onMoveNextRequest={() =>
            setGalleryState({
              slideIndex: (galleryState.slideIndex + 1) % props.block.images.length,
              isOpen: galleryState.isOpen
            })
          }
        ></GalleryModal>

      )}

    </SbEditable>
	);

}

export default Component


// Styles
const StyledComponent = styled.div<{displaySettings: any}>`

  .blockImageGallery-whiteBox{

    @media (min-width: ${theme.breakpoints[0]}px) and (max-width: ${theme.breakpoints[1]-1}px) {
      padding-top: 0 !important;
    }

    ${props => props.displaySettings.removeWhiteBox !== undefined &&
      css`
        padding-top: 0 !important;
      `
    }

  }

  .blockImageGallery-grid{

    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    margin-bottom: -20px;

    .blockImageGallery-grid-item{
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      width: 100%;
      margin-bottom: 20px;
    }

  }

  img{
    width: 100%;
  }

  .blockImageGallery-item-caption{
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 8px;
    padding-bottom: 8px;
    color: ${color.grey(20)};
    font-family: ${theme.typography.fontFamilySecondary};
    font-weight: bold;
    text-align: center;
  }

  .blockImageGallery-caption{
    margin-bottom: -30px;
    padding: 1rem 0;
    font-family: ${theme.typography.fontFamilySecondary};
    font-weight: bold;
    text-align: center;
  }


  @media (min-width: ${bp(2)}) {

    .blockImageGallery-grid{

      margin-left: -${multiplyValueWithUnits(theme.spacing.spacingHorizontal[1], 0.5)};
      margin-right: -${multiplyValueWithUnits(theme.spacing.spacingHorizontal[1], 0.5)};

      .blockImageGallery-grid-item{
        width: 50%;
        padding-left: ${multiplyValueWithUnits(theme.spacing.spacingHorizontal[1], 0.5)};
        padding-right: ${multiplyValueWithUnits(theme.spacing.spacingHorizontal[1], 0.5)};
      }

    }

    .blockImageGallery-caption{
      margin-top: 20px;
      margin-bottom: -30px;
    }

  }

    @media (min-width: ${bp(3)}) {

      .blockImageGallery-caption{
        margin-top: 20px;
        margin-bottom: -50px;
      }

    }

    @media (min-width: ${bp(4)}) {

      .blockImageGallery-grid{

        .blockImageGallery-grid-item{
          width: 33.3%;
          padding-left: 10px;
          padding-right: 10px;
        }

      }

    }

    @media (min-width: ${bp(5)}) {

      .blockImageGallery-grid{

        .blockImageGallery-grid-item{
          width: 25%;
          padding-left: 10px;
          padding-right: 10px;
        }

      }

    }


`